import { useMemo, type ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

interface PaginationBaseProps {
  readonly page: number; // 1-indexed
  readonly pageCount: number;
  /** How many sibling pages to show on each side of `page`. */
  readonly siblingCount?: number;
  readonly className?: string;
  readonly ariaLabel?: string;
}

export type PaginationProps = PaginationBaseProps &
  (
    | { readonly onChange: (page: number) => void; readonly hrefFor?: never }
    | {
        /**
         * Link mode: every page is an `<a href>` instead of a button, so the
         * pages are crawlable and work without JavaScript (static exports).
         */
        readonly hrefFor: (page: number) => string;
        readonly onChange?: never;
      }
  );

function buildRange(page: number, pageCount: number, siblings: number): (number | '…')[] {
  if (pageCount <= 1) return [1].slice(0, pageCount);
  const first = 1;
  const last = pageCount;
  const left = Math.max(page - siblings, first + 1);
  const right = Math.min(page + siblings, last - 1);
  const showLeftEllipsis = left > first + 1;
  const showRightEllipsis = right < last - 1;
  const out: (number | '…')[] = [first];
  if (showLeftEllipsis) out.push('…');
  for (let p = left; p <= right; p++) out.push(p);
  if (showRightEllipsis) out.push('…');
  if (last !== first) out.push(last);
  return out;
}

export function Pagination({
  page,
  pageCount,
  onChange,
  hrefFor,
  siblingCount = 1,
  className,
  ariaLabel = 'Pagination',
}: PaginationProps) {
  const items = useMemo(
    () => buildRange(page, pageCount, siblingCount),
    [page, pageCount, siblingCount],
  );

  const baseBtn =
    'inline-flex items-center justify-center size-8 rounded-md text-[12.5px] font-semibold transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25';
  const edgeBtn = cn(
    baseBtn,
    'text-ink-2 hover:bg-surface-2 disabled:opacity-45 disabled:cursor-not-allowed rtl:rotate-180',
  );

  // Previous / Next. Link mode renders a disabled end as an inert span: an
  // `<a>` without `href` is not focusable and `disabled` is not valid on it.
  const edge = (target: number, enabled: boolean, label: string, rel: string, icon: ReactNode) => {
    if (!hrefFor) {
      return (
        <button
          type="button"
          onClick={() => onChange?.(target)}
          disabled={!enabled}
          aria-label={label}
          className={edgeBtn}
        >
          {icon}
        </button>
      );
    }
    return enabled ? (
      <a href={hrefFor(target)} rel={rel} aria-label={label} className={edgeBtn}>
        {icon}
      </a>
    ) : (
      <span
        role="link"
        aria-label={label}
        aria-disabled="true"
        className={cn(edgeBtn, 'opacity-45')}
      >
        {icon}
      </span>
    );
  };

  return (
    <nav aria-label={ariaLabel} className={cn('inline-flex items-center gap-1', className)}>
      {edge(
        Math.max(1, page - 1),
        page > 1,
        'Previous page',
        'prev',
        <ChevronLeftIcon size={14} color="currentColor" />,
      )}
      {items.map((item, i) => {
        if (item === '…') {
          return (
            <span key={`e-${i}`} className="text-ink-3 px-1 select-none" aria-hidden="true">
              …
            </span>
          );
        }
        const pageClass = cn(
          baseBtn,
          item === page ? 'bg-primary text-primary-ink' : 'text-ink-2 hover:bg-surface-2',
        );
        return hrefFor ? (
          <a
            key={item}
            href={hrefFor(item)}
            aria-current={item === page ? 'page' : undefined}
            className={pageClass}
          >
            {item}
          </a>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange?.(item)}
            aria-current={item === page ? 'page' : undefined}
            className={pageClass}
          >
            {item}
          </button>
        );
      })}
      {edge(
        Math.min(pageCount, page + 1),
        page < pageCount,
        'Next page',
        'next',
        <ChevronRightIcon size={14} color="currentColor" />,
      )}
    </nav>
  );
}
